"use client";

import { useEffect, useRef, useState } from "react";
import { useCatalogViewport } from "./catalogViewport";

/** Same hold + crossfade used by Photo Editor fade-cycle cards. */
export const FADE_CYCLE_INTERVAL_MS = 1400;
export const FADE_CYCLE_CROSSFADE_MS = 500;

interface FadeCyclePreviewProps {
  images: string[];
  /** Ms each image stays visible before the next cut. */
  intervalMs?: number;
  /** Walk at most this many unique frames. Tool cards keep the default of 2. */
  maxFrames?: number;
  /** Delay before the first cut, so a grid does not flip in lockstep. */
  staggerMs?: number;
  className?: string;
  imageClassName?: string;
  alt?: string;
  priority?: boolean;
}

function uniqueImages(images: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const src of images) {
    if (!src || seen.has(src)) continue;
    seen.add(src);
    out.push(src);
  }
  return out;
}

/**
 * Poster is a normal <img> (browser loads it). Extra frames fetch only while
 * the card is on screen, then we cut through up to `maxFrames` portraits.
 */
export default function FadeCyclePreview({
  images,
  intervalMs = FADE_CYCLE_INTERVAL_MS,
  maxFrames = 2,
  staggerMs = 0,
  className = "",
  imageClassName = "object-cover object-top",
  alt = "",
  priority = false,
}: FadeCyclePreviewProps) {
  const slides = uniqueImages(images).slice(0, Math.max(1, maxFrames));
  const poster = slides[0] ?? "";
  const { ref, inView } = useCatalogViewport();
  const [index, setIndex] = useState(0);
  const readyRef = useRef<Record<number, boolean>>({ 0: true });

  useEffect(() => {
    if (!inView) setIndex(0);
  }, [inView]);

  useEffect(() => {
    if (!inView || slides.length < 2) return;
    let cancelled = false;
    let intervalId = 0;
    const startId = window.setTimeout(() => {
      if (cancelled) return;
      intervalId = window.setInterval(() => {
        setIndex((current) => {
          const len = slides.length;
          for (let step = 1; step <= len; step += 1) {
            const next = (current + step) % len;
            if (next === 0 || readyRef.current[next]) return next;
          }
          return current;
        });
      }, intervalMs);
    }, staggerMs);
    return () => {
      cancelled = true;
      window.clearTimeout(startId);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [inView, slides.length, intervalMs, staggerMs]);

  if (!poster) {
    return <div className={`h-full w-full bg-gray-100 ${className}`} />;
  }

  return (
    <div ref={ref} className={`absolute inset-0 overflow-hidden bg-gray-100 ${className}`}>
      {slides.map((src, i) => {
        const isPoster = i === 0;
        if (!isPoster && !inView) return null;
        const visible = (inView ? index : 0) === i;
        return (
          <img
            key={src}
            src={src}
            alt={isPoster ? alt : ""}
            className={`absolute inset-0 h-full w-full ${imageClassName}`}
            style={{
              opacity: visible ? 1 : 0,
              transition: `opacity ${FADE_CYCLE_CROSSFADE_MS}ms ease`,
            }}
            loading={isPoster && priority ? "eager" : isPoster ? "lazy" : "eager"}
            decoding="async"
            draggable={false}
            aria-hidden={!isPoster}
            onLoad={() => {
              readyRef.current[i] = true;
            }}
            ref={(el) => {
              if (el?.complete) readyRef.current[i] = true;
            }}
          />
        );
      })}
    </div>
  );
}
